import { useNavigate, useLocation } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import logo from "../../../assets/images/logo.png";
import UserMenu from "../common/UserMenu";
import { ChevronDown, Server, Tag, Search, Cpu, Menu, X } from "lucide-react";
import { useInfrastructureCache } from "../../hooks/useInfrastructureCache";

const NavbarProductInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("vms");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const { data: infrastructure, isLoading } = useInfrastructureCache();

  const getCurrentTab = () => {
    const pathParts = location.pathname.split("/");
    const lastPart = pathParts[pathParts.length - 1];
    const validTabs = ["Performance", "Finance", "Troubleshoot", "Security", "Backup"];
    return validTabs.includes(lastPart) ? lastPart : "Performance";
  };

  const currentTab = getCurrentTab();

  const getCurrentSelectionInfo = () => {
    const pathParts = location.pathname.split("/");
    const vmNameIndex = pathParts.indexOf("vm");
    const tagNameIndex = pathParts.indexOf("tag");
    
    if (vmNameIndex !== -1 && pathParts[vmNameIndex + 1]) {
      return {
        type: "vm",
        name: decodeURIComponent(pathParts[vmNameIndex + 1]),
        count: 1
      };
    }
    
    if (tagNameIndex !== -1 && pathParts[tagNameIndex + 1]) {
      const tagName = decodeURIComponent(pathParts[tagNameIndex + 1]);
      if (infrastructure) {
        const tag = infrastructure.tags.find(t => t.value === tagName);
        if (tag) {
          const vmCount = infrastructure.vmTags.filter(rel => rel.tagId === tag.id).length;
          return {
            type: "tag",
            name: tagName,
            count: vmCount
          };
        }
      }
      return {
        type: "tag",
        name: tagName,
        count: 0
      };
    }
    
    if (location.state?.vmNames) {
      if (location.state.vmNames.length === 1) {
        return {
          type: "vm",
          name: location.state.vmNames[0],
          count: 1
        };
      } else {
        return {
          type: "tag",
          name: "Selected Group",
          count: location.state.vmNames.length
        };
      }
    }
    
    return null;
  };

  const currentSelection = getCurrentSelectionInfo();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchTerm("");
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileMenuOpen]);

  if (isLoading || !infrastructure) {
    return (
      <nav className="sticky top-0 z-50 w-full bg-primary-dark shadow-lg flex justify-between items-center px-3 sm:px-5 py-2">
        <div className="flex items-center gap-2 sm:gap-4">
          <button 
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-80 transition-opacity"
          >
            <img
              src={logo}
              alt="Cloud Icon"
              className="w-8 sm:w-12 pt-1 pb-1 sm:pt-4 sm:pb-4 object-contain brightness-0 invert"
            />
            <div className="text-white text-base sm:text-2xl font-bold leading-tight">
              Sindi<span className="text-primary-cyan">Board</span>
            </div>
          </button>
        </div>
        <div className="pr-4">
          <UserMenu />
        </div>
      </nav>
    );
  }

  const { vms, tags, vmTags } = infrastructure;

  const getVmsWithDetails = () => {
    return vms.map(vm => ({
      ...vm,
      tags: vmTags
        .filter(relation => relation.vmId === vm.id)
        .map(relation => tags.find(tag => tag.id === relation.tagId))
        .filter(tag => tag)
    }));
  };

  const getTagsWithCounts = () => {
    return tags.map(tag => ({
      ...tag,
      vmCount: vmTags.filter(relation => relation.tagId === tag.id).length,
      vms: vmTags
        .filter(relation => relation.tagId === tag.id)
        .map(relation => vms.find(vm => vm.id === relation.vmId))
        .filter(vm => vm)
    }));
  };

  const vmsWithTags = getVmsWithDetails();
  const tagsWithCounts = getTagsWithCounts();

  const filteredVms = vmsWithTags.filter(vm =>
    vm.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTags = tagsWithCounts.filter(tag =>
    tag.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tag.key.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleVmSelect = (vm) => {
    setIsDropdownOpen(false);
    setSearchTerm("");
    setMobileMenuOpen(false);
    navigate(`/productDetail/vm/${encodeURIComponent(vm.name)}/${currentTab}`, {
      state: { vmIds: [vm.id], vmNames: [vm.name] }
    });
  };

  const handleTagSelect = (tag) => {
    setIsDropdownOpen(false);
    setSearchTerm("");
    setMobileMenuOpen(false);
    const vmIds = tag.vms.map(vm => vm.id);
    const vmNames = tag.vms.map(vm => vm.name);
    navigate(`/productDetail/tag/${encodeURIComponent(tag.value)}/${currentTab}`, {
      state: { vmIds, vmNames, tagName: tag.value, tagKey: tag.key }
    });
  };

  return (
    <nav className="sticky top-0 z-50 w-full bg-primary-dark shadow-lg">
      <div className="flex items-center justify-between px-3 sm:px-5 py-2 sm:py-3 md:py-4">
        <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
          <button 
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
          >
            <img
              src={logo}
              alt="Cloud Icon"
              className="w-8 sm:w-10 md:w-12 pt-1 pb-1 sm:pt-2 sm:pb-2 md:pt-4 md:pb-4 object-contain brightness-0 invert"
            />
            <div className="text-white text-base sm:text-xl md:text-2xl font-bold leading-tight whitespace-nowrap">
              Sindi<span className="text-primary-cyan">Board</span>
            </div>
          </button>

          {currentSelection && (
            <div className="hidden md:flex items-center gap-2 sm:gap-3 ml-2 px-2 sm:px-4 py-2 sm:py-3 bg-primary-cyan/10 rounded-xl border border-primary-cyan/30">
              <div className="flex items-center gap-1 sm:gap-2">
                {currentSelection.type === "vm" ? (
                  <Server size={14} className="sm:w-[18px] sm:h-[18px] text-primary-cyan" />
                ) : (
                  <Tag size={14} className="sm:w-[18px] sm:h-[18px] text-primary-cyan" />
                )}
                <span className="text-white text-xs sm:text-sm font-medium max-w-[120px] md:max-w-[200px] truncate">
                  {currentSelection.name}
                </span>
              </div>
              {currentSelection.count > 1 && (
                <div className="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 bg-primary-cyan rounded-full">
                  <span className="text-white text-[10px] sm:text-xs font-bold">{currentSelection.count}</span>
                  <span className="text-white text-[8px] sm:text-xs">VMs</span>
                </div>
              )}
            </div>
          )}

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 md:px-5 py-1.5 sm:py-2 md:py-3 bg-primary-cyan hover:bg-primary-cyan/80 rounded-lg transition-all text-white font-medium shadow-md text-xs sm:text-sm md:text-base"
            >
              <Cpu size={14} className="sm:w-[16px] sm:h-[16px] md:w-[18px] md:h-[18px]" />
              <span className="font-semibold hidden xs:inline">Switch Product</span>
              <span className="font-semibold xs:hidden">Switch</span>
              <ChevronDown size={12} className={`sm:w-[14px] sm:h-[14px] md:w-[16px] md:h-[16px] transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`} />
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full mt-2 left-0 w-[280px] sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50">
                <div className="flex border-b border-gray-200 bg-gray-50">
                  <button
                    onClick={() => setActiveCategory("vms")}
                    className={`flex-1 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1 sm:gap-2 ${
                      activeCategory === "vms"
                        ? "text-primary-cyan border-b-2 border-primary-cyan bg-white"
                        : "text-gray-600 hover:text-primary-dark"
                    }`}
                  >
                    <Server size={14} />
                    <span className="hidden xs:inline">Virtual Machines</span>
                    <span className="xs:hidden">VMs</span>
                  </button>
                  <button
                    onClick={() => setActiveCategory("tags")}
                    className={`flex-1 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1 sm:gap-2 ${
                      activeCategory === "tags"
                        ? "text-primary-cyan border-b-2 border-primary-cyan bg-white"
                        : "text-gray-600 hover:text-primary-dark"
                    }`}
                  >
                    <Tag size={14} />
                    Tags
                  </button>
                </div>

                <div className="p-2 sm:p-3 border-b border-gray-200 bg-white">
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder={`Search ${activeCategory === "vms" ? "VMs..." : "tags..."}`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-8 sm:pl-9 pr-2 sm:pr-3 py-1.5 sm:py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs sm:text-sm text-primary-dark placeholder-gray-400 focus:outline-none focus:border-primary-cyan focus:ring-1 focus:ring-primary-cyan"
                    />
                  </div>
                </div>

                <div className="max-h-80 sm:max-h-96 overflow-y-auto bg-white">
                  {activeCategory === "vms" ? (
                    filteredVms.length === 0 ? (
                      <div className="p-6 sm:p-8 text-center text-gray-500">
                        <Server size={24} className="mx-auto mb-2 opacity-40" />
                        <p className="text-xs sm:text-sm">No VMs found</p>
                      </div>
                    ) : (
                      filteredVms.map((vm) => (
                        <button
                          key={vm.id}
                          onClick={() => handleVmSelect(vm)}
                          className="w-full text-left px-3 sm:px-4 py-2 sm:py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 hover:border-l-4 hover:border-l-primary-cyan"
                        >
                          <div className="flex flex-col sm:flex-row justify-between items-start gap-1 sm:gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-primary-dark text-sm sm:text-base truncate">{vm.name}</p>
                              <div className="flex flex-wrap gap-1 sm:gap-2 mt-1">
                                <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full ${
                                  vm.status === "PowerState/deallocated" 
                                    ? "bg-red-100 text-red-600" 
                                    : "bg-green-100 text-green-600"
                                }`}>
                                  {vm.status === "PowerState/deallocated" ? "Stopped" : "Running"}
                                </span>
                                {vm.tags.slice(0, 2).map((tag) => (
                                  <span key={tag.id} className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full bg-primary-cyan/10 text-primary-cyan truncate max-w-[80px] sm:max-w-none">
                                    {tag.value}
                                  </span>
                                ))}
                                {vm.tags.length > 2 && (
                                  <span className="text-[10px] sm:text-xs text-gray-400">+{vm.tags.length - 2}</span>
                                )}
                              </div>
                            </div>
                            <span className="text-[10px] sm:text-xs text-gray-400 flex-shrink-0">{vm.vmType || "N/A"}</span>
                          </div>
                        </button>
                      ))
                    )
                  ) : (
                    filteredTags.length === 0 ? (
                      <div className="p-6 sm:p-8 text-center text-gray-500">
                        <Tag size={24} className="mx-auto mb-2 opacity-40" />
                        <p className="text-xs sm:text-sm">No tags found</p>
                      </div>
                    ) : (
                      filteredTags.map((tag) => (
                        <button
                          key={tag.id}
                          onClick={() => handleTagSelect(tag)}
                          className="w-full text-left px-3 sm:px-4 py-2 sm:py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 hover:border-l-4 hover:border-l-primary-cyan"
                        >
                          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1 sm:gap-2">
                                <Tag size={12} className="text-primary-cyan flex-shrink-0" />
                                <p className="font-medium text-primary-dark text-sm sm:text-base truncate">{tag.value}</p>
                              </div>
                              <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1 truncate">Key: {tag.key}</p>
                            </div>
                            <span className="text-[10px] sm:text-xs bg-primary-cyan/10 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-primary-cyan font-medium flex-shrink-0">
                              {tag.vmCount} VM{tag.vmCount !== 1 ? "s" : ""}
                            </span>
                          </div>
                        </button>
                      ))
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors text-white"
            aria-label="Menu"
          >
          </button>
          <div className="pr-0 sm:pr-2">
            <UserMenu />
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div ref={mobileMenuRef} className="md:hidden px-3 pb-3 border-t border-gray-700/50 pt-3">
          {currentSelection && (
            <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-primary-cyan/10 rounded-xl border border-primary-cyan/30">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {currentSelection.type === "vm" ? (
                  <Server size={14} className="text-primary-cyan flex-shrink-0" />
                ) : (
                  <Tag size={14} className="text-primary-cyan flex-shrink-0" />
                )}
                <span className="text-white text-sm font-medium truncate">{currentSelection.name}</span>
              </div>
              {currentSelection.count > 1 && (
                <div className="flex items-center gap-1 px-2 py-0.5 bg-primary-cyan rounded-full flex-shrink-0">
                  <span className="text-white text-xs font-bold">{currentSelection.count}</span>
                  <span className="text-white text-[10px]">VMs</span>
                </div>
              )}
            </div>
          )}

          <div className="relative w-full">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center justify-between gap-2 w-full px-4 py-2 bg-primary-cyan hover:bg-primary-cyan/80 rounded-lg transition-all text-white font-medium shadow-md text-sm"
            >
              <div className="flex items-center gap-2">
                <Cpu size={16} />
                <span className="font-semibold">Switch Product</span>
              </div>
              <ChevronDown size={16} className={`transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`} />
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50">
                <div className="flex border-b border-gray-200 bg-gray-50">
                  <button
                    onClick={() => setActiveCategory("vms")}
                    className={`flex-1 px-3 py-2 text-xs font-medium transition-all flex items-center justify-center gap-2 ${
                      activeCategory === "vms"
                        ? "text-primary-cyan border-b-2 border-primary-cyan bg-white"
                        : "text-gray-600 hover:text-primary-dark"
                    }`}
                  >
                    <Server size={14} />
                    VMs
                  </button>
                  <button
                    onClick={() => setActiveCategory("tags")}
                    className={`flex-1 px-3 py-2 text-xs font-medium transition-all flex items-center justify-center gap-2 ${
                      activeCategory === "tags"
                        ? "text-primary-cyan border-b-2 border-primary-cyan bg-white"
                        : "text-gray-600 hover:text-primary-dark"
                    }`}
                  >
                    <Tag size={14} />
                    Tags
                  </button>
                </div>

                <div className="p-2 border-b border-gray-200 bg-white">
                  <div className="relative">
                    <Search size={12} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder={`Search ${activeCategory === "vms" ? "VMs..." : "tags..."}`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-7 pr-2 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-primary-dark placeholder-gray-400 focus:outline-none focus:border-primary-cyan focus:ring-1 focus:ring-primary-cyan"
                    />
                  </div>
                </div>

                <div className="max-h-64 overflow-y-auto bg-white">
                  {activeCategory === "vms" ? (
                    filteredVms.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <Server size={20} className="mx-auto mb-2 opacity-40" />
                        <p className="text-xs">No VMs found</p>
                      </div>
                    ) : (
                      filteredVms.map((vm) => (
                        <button
                          key={vm.id}
                          onClick={() => handleVmSelect(vm)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <p className="font-medium text-primary-dark text-sm truncate">{vm.name}</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                              vm.status === "PowerState/deallocated" 
                                ? "bg-red-100 text-red-600" 
                                : "bg-green-100 text-green-600"
                            }`}>
                              {vm.status === "PowerState/deallocated" ? "Stopped" : "Running"}
                            </span>
                            {vm.tags.slice(0, 1).map((tag) => (
                              <span key={tag.id} className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary-cyan/10 text-primary-cyan truncate max-w-[80px]">
                                {tag.value}
                              </span>
                            ))}
                          </div>
                        </button>
                      ))
                    )
                  ) : (
                    filteredTags.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <Tag size={20} className="mx-auto mb-2 opacity-40" />
                        <p className="text-xs">No tags found</p>
                      </div>
                    ) : (
                      filteredTags.map((tag) => (
                        <button
                          key={tag.id}
                          onClick={() => handleTagSelect(tag)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1">
                                <Tag size={10} className="text-primary-cyan flex-shrink-0" />
                                <p className="font-medium text-primary-dark text-sm truncate">{tag.value}</p>
                              </div>
                              <p className="text-[10px] text-gray-500 truncate">Key: {tag.key}</p>
                            </div>
                            <span className="text-[10px] bg-primary-cyan/10 px-1.5 py-0.5 rounded-full text-primary-cyan font-medium flex-shrink-0 ml-2">
                              {tag.vmCount}
                            </span>
                          </div>
                        </button>
                      ))
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavbarProductInfo;